<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { Head, Link } from '@inertiajs/vue3';
import FrontLayout from '@/Layouts/FrontLayout.vue';

const props = defineProps({
    setting: Object,
    mobils: Array,
    features: Array,
    jenisMobils: Array,
    kecamatans: Array,
    ulasans: Array,
    news: Array,
    carousels: Array,
    totalUlasan: Number,
    // SEO Props
    title: String,
    meta_description: String,
    meta_keywords: String,
    og_image: String,
    canonical_url: String,
    // Schema Props
    total_ulasan: Number,
    overall_rating: [Number, String],
});

const wa = computed(() => props.setting?.setting_no_wa || '6281234567890');
const waUrl = computed(() => `https://wa.me/${wa.value}?text=Halo,%20saya%20ingin%20sewa%20mobil`);
const fmt = (v) => new Intl.NumberFormat('id-ID').format(v || 0);

// Computed counts for badges
const totalMobils = computed(() => props.mobils?.length || 0);
const totalUlasans = computed(() => props.ulasans?.length || 0);

const selectedType = ref('all');
const isMobileMenuOpen = ref(false);

const currentSlide = ref(0);
let carouselInterval = null;

const startCarousel = () => {
    if (props.carousels?.length > 1) {
        carouselInterval = setInterval(() => {
            currentSlide.value = (currentSlide.value + 1) % props.carousels.length;
        }, 5000);
    }
};

onMounted(() => {
    startCarousel();
});

onUnmounted(() => {
    if (carouselInterval) clearInterval(carouselInterval);
});

const filteredMobils = computed(() => {
    let list = props.mobils || [];
    if (selectedType.value !== 'all') {
        list = list.filter(m => m.jenis_mobil?.id == selectedType.value);
    }
    return list;
});

const toggleMobileMenu = () => {
    isMobileMenuOpen.value = !isMobileMenuOpen.value;
};

// JSON-LD Schema
const jsonLd = computed(() => {
    const setting = props.setting || {};
    const schema = {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "name": setting.setting_title || 'Gotransport Banyuwangi',
        "description": props.meta_description,
        "url": props.canonical_url,
        "logo": setting.setting_logo ? `/storage/logo/${setting.setting_logo}` : '',
        "image": props.og_image,
        "telephone": setting.setting_no_telp || setting.setting_no_wa,
        "email": setting.setting_email,
        "address": {
            "@type": "PostalAddress",
            "streetAddress": setting.setting_alamat || 'Banyuwangi',
            "addressLocality": "Banyuwangi",
            "addressRegion": "Jawa Timur",
            "postalCode": "68400",
            "addressCountry": "ID"
        },
        "geo": {
            "@type": "GeoCoordinates",
            "latitude": -8.2191,
            "longitude": 114.3691
        },
        "priceRange": "Rp300.000 - Rp1.500.000",
        "openingHours": "Mo-Su 24:00",
        "areaServed": {
             "@type": "City",
             "name": "Banyuwangi"
        }
    };

    if (props.total_ulasan > 0) {
        schema.aggregateRating = {
            "@type": "AggregateRating",
            "ratingValue": props.overall_rating,
            "reviewCount": props.total_ulasan,
            "bestRating": 5,
            "worstRating": 1
        };
    }

    return schema;
});

</script>

<template>
    <FrontLayout 
        :setting="props.setting" 
        :title="props.title" 
        :metaDescription="props.meta_description"
    >
        <Head>

            <meta name="keywords" :content="meta_keywords" />
            <link rel="canonical" :href="canonical_url" />

            <!-- Open Graph / Facebook -->
            <meta property="og:type" content="website" />
            <meta property="og:url" :content="canonical_url" />
            <meta property="og:title" :content="title" />
            <meta property="og:description" :content="meta_description" />
            <meta property="og:image" :content="og_image" />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta property="og:image:type" content="image/jpeg" />
            <meta property="og:image:alt" :content="title" />
            <meta property="og:site_name" content="Gotransport Banyuwangi" />

            <!-- Twitter -->
            <meta property="twitter:card" content="summary_large_image" />
            <meta property="twitter:url" :content="canonical_url" />
            <meta property="twitter:title" :content="title" />
            <meta property="twitter:description" :content="meta_description" />
            <meta property="twitter:image" :content="og_image" />
            
            <!-- JSON-LD Structured Data -->
            <component :is="'script'" type="application/ld+json" v-html="JSON.stringify(jsonLd)"></component>
        </Head>
        
        <!-- Hero Section - Modern Design (Ported from Astro) -->
        <section id="hero-section" class="relative overflow-hidden">
            <!-- Main Hero Background with Gradient -->
            <div class="relative bg-gradient-to-br from-orange-600 via-orange-500 to-amber-400 min-h-[500px] lg:min-h-[550px]">
                <!-- Decorative Background Elements -->
                <div class="absolute inset-0 overflow-hidden">
                    <!-- Large decorative star -->
                    <svg class="absolute left-4 lg:left-12 top-20 w-16 h-16 lg:w-24 lg:h-24 text-white/20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                    </svg>
                    <!-- Small decorative dots -->
                    <div class="absolute right-1/4 top-32 w-2 h-2 bg-white/30 rounded-full"></div>
                    <div class="absolute right-1/3 top-40 w-1.5 h-1.5 bg-white/20 rounded-full"></div>
                    <div class="absolute left-1/4 bottom-32 w-2 h-2 bg-white/25 rounded-full"></div>
                    <!-- Gradient overlay for depth -->
                    <div class="absolute inset-0" style="background: radial-gradient(circle at 70% 30%, rgba(255,255,255,0.1) 0%, transparent 50%);"></div>
                </div>

                <!-- Hero Content -->
                <div class="container mx-auto px-4 lg:px-6 xl:px-8 max-w-7xl relative z-10">
                    <div class="flex flex-col gap-8 lg:gap-12 py-12 lg:pt-16 lg:pb-24">
                        
                        <!-- Top Row: 2 Columns Content -->
                        <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                            
                            <!-- Left Column - Text Content -->
                            <div class="text-white order-2 lg:order-1">
                                <!-- Main Headline -->
                                <h1 class="text-2xl md:text-3xl lg:text-4xl font-extrabold leading-tight mb-2 tracking-tight" style="text-shadow: 0 2px 4px rgba(0,0,0,0.15);">
                                    Sewa Mobil Banyuwangi<br/>
                                    <span class="text-white">Rental Mobil Banyuwangi</span>
                                </h1>
                                <h2 class="text-base md:text-lg font-bold text-amber-200 mb-2 tracking-wide">
                                    Solusi Untuk Masalah Transportasi Anda di Banyuwangi
                                </h2>
                                <p class="text-xs md:text-sm font-medium text-white/90 mb-4 border-l-4 border-amber-400 pl-3">
                                    Murah, Terpercaya, Armada Lengkap & Pelayanan Profesional
                                </p>
                                
                                <!-- Subtitle / Description -->
                                <p class="text-sm text-white/90 mb-6 max-w-xl leading-relaxed">
                                    {{ setting?.setting_title || 'Gotransport' }} - Solusi perjalanan wisata terbaik dengan armada mobil modern, driver berpengalaman, dan harga kompetitif.
                                </p>
                                
                                <!-- CTA Buttons -->
                                <div class="flex flex-row gap-3 mb-4">
                                    <a 
                                        :href="waUrl"
                                        target="_blank"
                                        rel="nofollow noopener"
                                        class="inline-flex items-center justify-center gap-2 bg-amber-400 hover:bg-amber-300 text-gray-900 font-bold px-4 py-3 text-sm md:text-base md:px-8 md:py-3.5 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5 whitespace-nowrap flex-1 md:flex-none"
                                    >
                                        <i class="fa fa-calendar-check-o"></i>
                                        Booking <span class="hidden md:inline">Sekarang</span>
                                    </a>
                                    <a 
                                        href="#armada"
                                        class="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 border-2 border-white/50 text-white font-bold px-4 py-3 text-sm md:text-base md:px-8 md:py-3.5 rounded-full transition-all duration-300 backdrop-blur-sm whitespace-nowrap flex-1 md:flex-none"
                                    >
                                        <i class="fa fa-search"></i>
                                        <span class="md:hidden">Armada</span><span class="hidden md:inline">Lihat Armada</span>
                                    </a>
                                </div>
                            </div>
                            
                            <!-- Right Column - Featured Image with Badges -->
                            <div class="relative order-1 lg:order-2 flex justify-center lg:justify-end mb-8 lg:mb-0">
                                <!-- Image Container with Rotation Effect -->
                                <div class="relative w-full max-w-md mx-auto lg:mr-0 lg:ml-auto">
                                    <!-- Main Featured Image -->
                                    <div class="relative transform lg:rotate-2 transition-transform duration-500 hover:rotate-0 z-10">
                                        <!-- Note: Border restored matching Astro source -->
                                        <div class="bg-white rounded-2xl shadow-2xl overflow-hidden border-[6px] border-white/40 backdrop-blur-sm aspect-[4/3]">
                                            <div v-if="carousels?.length > 0" class="w-full h-full relative">
                                                <transition-group name="fade">
                                                    <div v-for="(slide, index) in carousels" :key="slide.id" v-show="currentSlide === index" class="absolute inset-0 w-full h-full">
                                                        <img 
                                                            :src="`/storage/carousel/${slide.carousel_image}`" 
                                                            class="w-full h-full object-cover" 
                                                            :alt="slide.carousel_title" 
                                                            @error="$event.target.src = 'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?q=80&w=2070&auto=format&fit=crop'"
                                                        />
                                                    </div>
                                                </transition-group>
                                            </div>
                                            <img v-else src="https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?q=80&w=2070&auto=format&fit=crop" class="w-full h-full object-cover" />
                                        </div>
                                        
                                        <!-- Fleet Count Badge -->
                                        <div class="absolute -bottom-6 -left-2 lg:-left-6 bg-white rounded-xl shadow-xl px-5 py-3 flex items-center gap-3 z-20">
                                            <div class="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                                                <i class="fa fa-car text-orange-600 text-xl"></i>
                                            </div>
                                            <div>
                                                <p class="text-xl font-extrabold text-gray-900 leading-none">{{ totalMobils }}+ Mobil</p>
                                                <p class="text-xs font-bold text-gray-500 uppercase tracking-wider mt-1">Armada Siap</p>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <!-- Rating Badge -->
                                    <div class="absolute -top-6 -right-2 lg:-right-6 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl shadow-xl px-5 py-3 text-white z-20 transform rotate-3 hover:rotate-0 transition-transform">
                                        <div class="flex items-center gap-1.5 mb-1">
                                            <i class="fa fa-star text-base text-yellow-200"></i>
                                            <span class="font-bold text-2xl">4.8/5</span>
                                        </div>
                                        <p class="text-xs font-bold text-white/90 uppercase tracking-wider text-right">{{ totalUlasan > 0 ? totalUlasan : '100+' }} Ulasan Google</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <!-- Bottom Row: Opening Text (Integrated) -->
                        <div class="relative z-10 mt-4 lg:mt-8">
                            <div class="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-4 shadow-lg max-w-4xl mx-auto">
                                <div class="prose prose-sm lg:prose-base max-w-none text-white text-sm lg:text-base leading-relaxed text-center font-medium drop-shadow-sm">
                                    <p v-if="setting?.setting_opening_text" v-html="setting.setting_opening_text"></p>
                                    <div v-else>
                                        <p class="mb-2">
                                            <strong class="text-amber-200">Sewa mobil Banyuwangi murah.</strong> Sewa mobil lepas kunci atau <strong class="text-amber-200">sewa mobil dengan sopir</strong>. 
                                            Sewa mobil Avanza, Xenia, Hiace juga mobil lainnya. Sewa mobil untuk aktifitas harian. 
                                            Sewa mobil untuk agenda mingguan. Sewa mobil untuk kontrak bulanan.
                                        </p>
                                        <p class="mb-0">
                                            Siap antar jemput lokasi (<strong class="text-amber-200">Bandara, Pelabuhan, Stasiun, Hotel</strong>). 
                                            Layanan menjangkau seluruh wilayah <strong class="text-amber-200">Banyuwangi</strong> dan kota-kota lain di Indonesia.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <!-- Wave Bottom Shape -->
                <div class="absolute bottom-0 left-0 right-0 pointer-events-none">
                    <svg viewBox="0 0 1440 320" fill="none" class="w-full h-auto absolute bottom-0 z-0" preserveAspectRatio="none" style="height: 180px; opacity: 0.6;">
                        <path d="M0,320L48,277.3C96,235,192,149,288,117.3C384,85,480,107,576,133.3C672,160,768,192,864,197.3C960,203,1056,181,1152,160C1248,139,1344,117,1392,106.7L1440,96V320H1392C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320H0Z" fill="url(#paint0_linear)"/>
                        <defs>
                            <linearGradient id="paint0_linear" x1="720" y1="0" x2="720" y2="320" gradientUnits="userSpaceOnUse">
                                <stop stop-color="white" stop-opacity="0"/>
                                <stop offset="1" stop-color="white" stop-opacity="0.2"/>
                            </linearGradient>
                        </defs>
                    </svg>
                    <svg viewBox="0 0 1440 200" fill="none" class="w-full h-auto absolute bottom-0" preserveAspectRatio="none" style="height: 120px;">
                        <path d="M0 200L60 180C120 160 240 120 360 100C480 80 600 80 720 90C840 100 960 120 1080 130C1200 140 1320 140 1380 140L1440 140V200H1380C1320 200 1200 200 1080 200C960 200 840 200 720 200C600 200 480 200 360 200C240 200 120 200 60 200H0Z" fill="rgba(255,255,255,0.1)"/>
                    </svg>
                    <svg viewBox="0 0 1440 200" fill="none" class="w-full h-auto absolute bottom-0" preserveAspectRatio="none" style="height: 100px;">
                        <path d="M0 200L60 170C120 140 240 80 360 50C480 20 600 20 720 35C840 50 960 80 1080 95C1200 110 1320 110 1380 110L1440 110V200H1380C1320 200 1200 200 1080 200C960 200 840 200 720 200C600 200 480 200 360 200C240 200 120 200 60 200H0Z" fill="rgba(255,255,255,0.15)"/>
                    </svg>
                    <svg viewBox="0 0 1440 200" fill="none" class="w-full h-auto relative" preserveAspectRatio="none" style="height: 80px;">
                        <path d="M0 200L48 165C96 130 192 60 288 30C384 0 480 10 576 25C672 40 768 60 864 70C960 80 1056 80 1152 75C1248 70 1344 60 1392 55L1440 50V200H1392C1344 200 1248 200 1152 200C1056 200 960 200 864 200C768 200 672 200 576 200C480 200 384 200 288 200C192 200 96 200 48 200H0Z" fill="rgba(255,255,255,0.2)"/>
                    </svg>
                </div>
            </div>
        </section>

        <!-- Car Section (Mobil Grid) -->
        <section id="armada" class="py-8 md:py-10 bg-slate-100 dark:bg-gray-900 border-t border-slate-200 dark:border-gray-800 transition-colors duration-300">
            <div class="container mx-auto px-4 lg:px-6 xl:px-8 max-w-7xl">
                <!-- Section Header -->
                <div class="text-center mb-6 md:mb-8">
                    <span class="inline-block px-3 py-1 bg-blue-50 text-blue-700 text-xs font-medium mb-2 rounded-full">Pilihan Armada</span>
                    <h2 class="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-2 transition-colors">Pilihan Mobil Sewa di Banyuwangi</h2>
                    <div class="flex flex-col md:flex-row items-center justify-center gap-4 max-w-2xl mx-auto">
                        <p class="text-gray-600 dark:text-gray-400 text-sm transition-colors">Pilih mobil sesuai kebutuhan Anda. Tersedia berbagai tipe mulai dari city car, MPV keluarga, hingga SUV untuk perjalanan wisata di Banyuwangi.</p>
                    </div>

                </div>

                <!-- Grid -->
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div v-for="mobil in filteredMobils" :key="mobil.id" class="group bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl overflow-hidden hover:border-orange-400 hover:shadow-xl transition-all">
                        <!-- Gambar Mobil -->
                        <div class="relative aspect-[4/3] bg-white overflow-hidden border-b border-gray-200">
                            <span v-if="mobil.mobil_harga_sewa || mobil.mobil_harga_include" class="absolute top-3 right-3 bg-green-700 text-white px-3 py-1.5 text-xs font-bold rounded-lg shadow-md z-10">✓ Tersedia</span>
                            <img :src="`/storage/mobil/${mobil.mobil_foto}`" :alt="mobil.mobil_nama" class="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500" />
                        </div>

                        <!-- Info Mobil -->
                        <div class="p-4 bg-white dark:bg-gray-800 group-hover:bg-orange-50/30 dark:group-hover:bg-gray-700/30 transition-colors">
                            <h3 class="text-lg font-bold text-gray-900 dark:text-white mb-2 group-hover:text-orange-600 transition-colors">{{ mobil.mobil_nama }}</h3>
                            
                            <!-- Jenis Mobil -->
                            <div v-if="mobil.jenis_mobil" class="mb-3">
                                <span class="inline-block px-3 py-1 bg-blue-100 text-blue-700 text-xs font-medium border border-blue-200 rounded-lg">{{ mobil.jenis_mobil.jenis_nama }}</span>
                            </div>

                            <!-- Harga -->
                            <div class="mb-4 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 border border-gray-200 dark:border-gray-600 divide-y divide-gray-200 dark:divide-gray-600 rounded-xl overflow-hidden mt-auto">
                                <div v-if="mobil.mobil_harga_sewa" class="flex items-center justify-between text-sm p-3">
                                    <span class="text-gray-700 dark:text-gray-300 flex items-center gap-2 font-medium">
                                        <span class="w-6 h-6 bg-orange-100 dark:bg-gray-700 flex items-center justify-center text-orange-600 dark:text-orange-400 border border-orange-200 dark:border-gray-600 rounded-lg"><i class="fa fa-key text-xs"></i></span>
                                        Lepas Kunci
                                    </span>
                                    <span class="font-bold text-gray-900 dark:text-gray-100">Rp {{ fmt(mobil.mobil_harga_sewa) }}</span>
                                </div>
                                <div v-if="mobil.mobil_harga_include" class="flex items-center justify-between text-sm p-3">
                                    <span class="text-gray-700 dark:text-gray-300 flex items-center gap-2 font-medium">
                                        <span class="w-6 h-6 bg-blue-100 dark:bg-gray-700 flex items-center justify-center text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-gray-600 rounded-lg"><i class="fa fa-user text-xs"></i></span>
                                        Dengan Sopir
                                    </span>
                                    <span class="font-bold text-gray-900 dark:text-gray-100">Rp {{ fmt(mobil.mobil_harga_include) }}</span>
                                </div>
                            </div>

                            <!-- Button Sewa -->
                            <a :href="waUrl" target="_blank" class="block w-full bg-green-700 hover:bg-green-800 text-white font-bold py-3 px-4 text-center transition-colors rounded-xl">
                                <i class="fa fa-whatsapp mr-2"></i>Sewa Sekarang
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Feature Section -->
        <section id="features" class="py-8 md:py-10 bg-blue-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 transition-colors duration-300">
            <div class="container mx-auto px-4 lg:px-6 xl:px-8 max-w-7xl">
                <div class="text-center mb-6">
                    <span class="inline-block px-3 py-1 bg-purple-50 text-purple-700 text-xs font-medium mb-2">Keunggulan Layanan Kami</span>
                    <h3 class="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-2 transition-colors">Kenapa Sewa Mobil di Banyuwangi Bersama {{ setting?.setting_title || 'Gotransport' }}?</h3>
                </div>
                <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
                    <Link v-for="(f, i) in features" :key="i" :href="`/feature/${f.feature_slug}`" class="flex flex-col items-center justify-center p-4 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-2xl hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-gray-700 transition-all duration-300 text-center group cursor-pointer hover:shadow-md">
                        <div class="w-16 h-16 flex items-center justify-center bg-blue-50 dark:bg-gray-700/50 rounded-2xl mb-4 group-hover:bg-white dark:group-hover:bg-gray-600 group-hover:text-blue-600 transition-all duration-300">
                            <i :class="f.feature_icon || 'fa fa-check'" class="text-3xl text-blue-600 transition-colors duration-300"></i>
                        </div>
                        <span class="text-base font-bold text-gray-800 dark:text-gray-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors leading-snug">{{ f.feature_title }}</span>
                    </Link>
                </div>
            </div>
        </section>
        
        <!-- Area Layanan (New from Astro) -->
        <section v-if="kecamatans?.length" id="area-layanan" class="py-8 md:py-10 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 transition-colors duration-300">
             <div class="container mx-auto px-4 lg:px-6 xl:px-8 max-w-7xl">
                <div class="text-center mb-6">
                    <span class="inline-block px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-medium mb-2">Jangkauan Luas</span>
                    <h3 class="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-1 transition-colors">Jangkauan Layanan Sewa Mobil di Banyuwangi</h3>
                    <p class="text-gray-600 dark:text-gray-400 text-sm transition-colors">Layanan antar jemput armada untuk seluruh wilayah kecamatan di Banyuwangi</p>
                </div>
                <div class="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl shadow-sm transition-colors">
                    <div class="max-h-[400px] overflow-y-auto p-6 md:p-8 custom-scrollbar">
                         <div class="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                             <Link v-for="kec in kecamatans" :key="kec.id" :href="`/area-layanan/${kec.slug}`" class="group flex flex-col items-center text-center p-4 border border-gray-100 dark:border-gray-700 rounded-2xl bg-white dark:bg-gray-900 hover:border-orange-400 hover:bg-orange-50 dark:hover:bg-gray-700 transition-all duration-300">
                                <div class="w-12 h-12 bg-orange-50 dark:bg-gray-800 rounded-xl flex items-center justify-center text-orange-600 mb-3 group-hover:bg-white dark:group-hover:bg-gray-600 transition-all duration-300">
                                    <i class="fa fa-map-marker text-xl"></i>
                                </div>
                                <span class="text-sm font-bold text-gray-900 dark:text-gray-100 group-hover:text-orange-600 transition-colors leading-tight">{{ kec.nama_kecamatan }}</span>
                             </Link>
                         </div>
                    </div>
                </div>
             </div>
        </section>
    </FrontLayout>
</template>

<style scoped>
.fade-enter-active, .fade-leave-active { transition: opacity 0.8s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
</style>
